import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = ["id", "min", "max", "value", "disabled", "placeholder"]
const _hoisted_3 = {
  key: 0,
  class: "errorMessage",
  "aria-live": "polite"
}

import { ref, computed, watch } from 'vue';
import { Skeletor } from 'vue-skeletor';
import 'vue-skeletor/dist/vue-skeletor.css';
import {
  parseISO,
  isValid,
  format,
  endOfYear,
  addYears,
  isSameDay,
} from 'date-fns';
import {
  toZonedTime, formatInTimeZone,
} from 'date-fns-tz';
import { utcDate, utcDateTime } from '../..';
import { timezone } from '../../envVariables';


export default /*@__PURE__*/_defineComponent({
  __name: 'DatePickerCalendar',
  props: {
    id: { default: `datepicker-${Date.now()}` },
    initialDate: {},
    disabled: { type: Boolean, default: false },
    todayText: {},
    dateFormat: {},
    teleportId: {},
    maxDate: {},
    minDate: {},
    isLoading: { type: Boolean, default: false },
    isError: { type: Boolean, default: false },
    resetField: { type: Boolean }
  },
  emits: ['update:selectedDate'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const now = new Date();
const resetDate = props.initialDate;

const isIOS = (() => {
  const platform = navigator.platform || '';
  const userAgent = navigator.userAgent || navigator.vendor || '';
  const isOldIOS = /iPad|iPhone|iPod/.test(userAgent) || /iPad|iPhone|iPod/.test(platform);
  const isModernIOS = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isOldIOS || isModernIOS;
})();

const isAndroid = (() => {
  const userAgent = navigator.userAgent || '';
  return /android/i.test(userAgent);
})();

const minDateToUse = computed(() =>
  // eslint-disable-next-line max-len
  (props.minDate ? parseISO(props.minDate) : new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));

const maxDateToUse = computed(() =>
  (props.maxDate
    ? parseISO(props.maxDate)
    : endOfYear(addYears(utcDateTime(new Date()), 1))));

const localDateValue = ref('');

const selectedDate = computed({
  get: () => localDateValue.value,
  set: (value) => {
    const ukDate = toZonedTime(value, timezone);
    const dateAsUtc = new Date(Date.UTC(ukDate.getFullYear(), ukDate.getMonth(), ukDate.getDate()));
    localDateValue.value = dateAsUtc.toISOString();
    emit('update:selectedDate', localDateValue.value);
  },
});

watch(() => props.initialDate, (value) => {
  localDateValue.value = isValid(parseISO(value))
    ? formatInTimeZone(value, timezone, 'yyyy-MM-dd')
    : utcDate(new Date()).toISOString();
}, { immediate: true });

const formatDate = (date: Date) => formatInTimeZone(
  toZonedTime(date, timezone),
  timezone,
  props.dateFormat,
);

const rawInputValue = ref('');

// Convert to yyyy-MM-dd format for native input
const inputDate = computed(() => rawInputValue.value || (localDateValue.value
  ? formatInTimeZone(localDateValue.value, timezone, 'yyyy-MM-dd')
  : ''));

const minDateString = computed(() => format(minDateToUse.value, 'yyyy-MM-dd'));
const maxDateString = computed(() => format(maxDateToUse.value, 'yyyy-MM-dd'));

const minDateStringUK = computed(() => format(minDateToUse.value, 'dd / MM / yyyy'));
const maxDateStringUK = computed(() => format(maxDateToUse.value, 'dd / MM / yyyy'));

const formattedAriaLabel = computed(() => {
  const todayFormatted = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  if (inputDate.value === todayFormatted) {
    return props.todayText; // e.g., "Today"
  }
  return formatDate(new Date(localDateValue.value));
});

const isDateOutOfRange = ref(false);

const onInputBlur = (event: Event) => {
  const { value } = event.target as HTMLInputElement;
  rawInputValue.value = value;

  const parsed = parseISO(value);

  if (isValid(parsed)) {
    const isInRange = (
      isSameDay(parsed, minDateToUse.value) || parsed >= minDateToUse.value
    ) && parsed <= maxDateToUse.value;
    isDateOutOfRange.value = !isInRange;

    if (isInRange) {
      const ukDate = toZonedTime(parsed, timezone);
      // eslint-disable-next-line max-len
      const dateAsUtc = new Date(Date.UTC(ukDate.getFullYear(), ukDate.getMonth(), ukDate.getDate()));
      localDateValue.value = dateAsUtc.toISOString();
      emit('update:selectedDate', localDateValue.value);
    }
  } else {
    isDateOutOfRange.value = true;
    localDateValue.value = '';
  }
};

watch(() => props.resetField, (reset) => {
  if (reset) {
    rawInputValue.value = '';
    localDateValue.value = isValid(parseISO(resetDate))
      ? formatInTimeZone(resetDate, timezone, 'yyyy-MM-dd')
      : utcDate(new Date()).toISOString();

    isDateOutOfRange.value = false;
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  return (_ctx.isLoading)
    ? (_openBlock(), _createBlock(_unref(Skeletor), { key: 0 }))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        id: "vueDatepicker",
        class: _normalizeClass(["datepickerCalendar", { error: _ctx.isError }])
      }, [
        _createElementVNode("label", {
          id: "datepickerLabel",
          for: _ctx.id,
          class: "srOnly"
        }, _toDisplayString(selectedDate.value ? `Choose a date, currently selected: ${formattedAriaLabel.value}` : _ctx.todayText), 9, _hoisted_1),
        _createElementVNode("input", {
          id: _ctx.id,
          class: _normalizeClass(["datepickerInput", {
        iosDateInput: _unref(isIOS),
        androidDateInput: _unref(isAndroid)
      }]),
          type: "date",
          min: minDateString.value,
          max: maxDateString.value,
          value: inputDate.value,
          disabled: _ctx.disabled,
          placeholder: _ctx.todayText,
          "aria-labelledby": "datepickerLabel",
          onBlur: onInputBlur,
          onInput: _cache[0] || (_cache[0] = (event) => _unref(isAndroid) && onInputBlur(event))
        }, null, 42, _hoisted_2),
        (isDateOutOfRange.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, " Please enter a date between " + _toDisplayString(minDateStringUK.value) + " and " + _toDisplayString(maxDateStringUK.value) + ". ", 1))
          : _createCommentVNode("", true)
      ], 2))
}
}

})